import { iframeDataBridge } from '../iframe/iframeDataBridge';
import { useTranslator } from './useTranslator';
import axios from 'axios';

/**
 * Utility functions for handling appointments and delivery locations
 */

/**
 * Opens a delivery location in edit mode
 * @param {string} deliveryLocationId - The ID of the delivery location to edit
 * @param {function} onComplete - Optional callback to execute after opening the location
 */
export function handleEditDeliveryLocation(deliveryLocationId, onComplete = null) {
	try {
		iframeDataBridge.getIframeWindow().amplify.publish('evtPOSSelected', {
			id: deliveryLocationId,
			editMode: true,
			onlyModal: true,
		});

		// Execute callback if provided
		if (onComplete && typeof onComplete === 'function') {
			onComplete();
		}
	} catch (error) {
		console.error('Error opening delivery location in edit mode:', error);
	}
}

/**
 * Creates a new visit/appointment for a delivery location
 * @param {Object} visitData - Data containing deliveryLocationId and other visit information
 */
export function handleAddNewVisit(visitData) {
	try {
		const sfaHelper = iframeDataBridge.getSfaHelper();
		sfaHelper.createAppointmentForDL(visitData.deliveryLocationId, visitData.selectedDate || null, visitData.slot || null);
	} catch (error) {
		console.error('Error creating new visit:', error);
	}
}

export function createManualAppointmentForDL(visitData) {}

/**
 * Handles phone number copy action and shows success toast
 * @param {string} phoneNumber - The phone number that was copied (optional, for logging)
 */
export function handlePhoneNumberCopied(phoneNumber) {
	const t = useTranslator();
	iframeDataBridge.getIframeWindow().toast(t('PhoneNumberCopied'), 'success', true);
}

/**
 * Gets the appropriate icon for a delivery location based on its type
 * @param {Object} deliveryLocation - The delivery location object
 * @returns {string} - Material Design icon name
 */
export function getDeliveryLocationTypeIcon(deliveryLocation) {
	const deliveryLocationTypes = iframeDataBridge.getEcosVM().deliveryLocationTypes;
	const deliveryLocationType = deliveryLocationTypes?.find((type) => type.id === deliveryLocation.deliveryLocationTypeId);

	if (!deliveryLocationType?.reference) {
		return 'storefront';
	}

	// If it's not a system type, use default location icon
	if (!deliveryLocationType.system) {
		return 'location_on';
	}

	const typeReference = deliveryLocationType.reference.toUpperCase();

	// Map system delivery location type references to Material Design icons
	const iconMap = {
		STORE: 'storefront',
		ADR: 'location_on',
		DOM: 'home',
		ESR: 'location_on',
		BOX: 'inventory_2',
		NIGHT: 'hotel',
		PROSPECT: 'person_search',
	};

	return iconMap[typeReference] || 'storefront';
}

/**
 * Gets the appropriate color for a delivery location based on its type
 * @param {Object} deliveryLocation - The delivery location object
 * @returns {string} - Hexadecimal color code
 */
export function getDeliveryLocationTypeColor(deliveryLocation, appointment = null) {
	if (appointment) {
		const sfaHelper = iframeDataBridge.getSfaHelper();
		return sfaHelper.getAppointmentColor(appointment);
	}

	const colors = ['#1F77B4', '#FF7F0E', '#2CA02C', '#D62728', '#9467BD', '#8C564B', '#E377C2', '#7F7F7F', '#BCBD22', '#17BECF', '#0D3B66', '#2EC4B6', '#C44536', '#FFD166', '#4CC9F0', '#B5179E', '#6A994E', '#E36414', '#003049', '#FFADAD', '#80ED99', '#CDB4DB', '#00B4D8', '#EF233C', '#EAE2B7', '#3A0CA3', '#1B4332', '#F4A261', '#495057', '#FF006E', '#90DBF4', '#A7C957'];

	const deliveryLocationTypes = iframeDataBridge.getEcosVM().deliveryLocationTypes;
	const deliveryLocationType = deliveryLocationTypes?.find((type) => type.id === deliveryLocation.deliveryLocationTypeId);

	if (!deliveryLocationType?.reference) {
		return colors[0];
	}

	if (deliveryLocationType.system) {
		const systemTypeOrder = ['STORE', 'ADR', 'DOM', 'ESR', 'BOX', 'NIGHT', 'PROSPECT'];
		const typeReference = deliveryLocationType.reference.toUpperCase();
		const index = systemTypeOrder.indexOf(typeReference);

		if (index !== -1) {
			return colors[index];
		}

		return colors[0];
	} else {
		const customStartIndex = 7;
		const customTypes = deliveryLocationTypes.filter((type) => !type.system && type.active);
		const sortedCustomTypes = customTypes.sort((a, b) => a.id - b.id);
		const customIndex = sortedCustomTypes.findIndex((type) => type.id === deliveryLocation.deliveryLocationTypeId);

		if (customIndex !== -1) {
			const colorIndex = customStartIndex + (customIndex % (colors.length - customStartIndex));
			return colors[colorIndex];
		}

		return colors[customStartIndex];
	}
}

/** Gets color based on KPI percentage
 * @param {number} percentage - KPI percentage
 * @returns {string} - Hexadecimal color code
 */
export function getKPIColor(percentage) {
	if (percentage >= 90) return '#28a745';
	if (percentage >= 70) return '#ffc107';
	if (percentage >= 50) return '#fd7e14';
	return '#dc3545';
}

/** Gets the visit frequency for a delivery location
 * @param {string} deliveryLocationId - The ID of the delivery location
 * @returns {Promise<string|null>} - Visit frequency text or null if not available
 */
export async function getVisitFrequency(deliveryLocationId) {
	return new Promise((resolve) => {
		const sfaHelper = iframeDataBridge.getSfaHelper();
		if (!sfaHelper || !sfaHelper.kpi || !sfaHelper.kpi.business || !sfaHelper.kpi.business.visitRate) {
			resolve(null);
			return;
		}

		const currentUser = iframeDataBridge.getCurrentUser();
		if (!currentUser) {
			resolve(null);
			return;
		}

		try {
			sfaHelper.kpi.business.visitRate.getCurrentVisitRateForDL(
				deliveryLocationId,
				(result) => {
					resolve(result ? result.text + result.infos || result.parameters : null);
				},
				currentUser.id
			);
		} catch (e) {
			resolve(null);
		}
	});
}

/** Gets the next scheduled visit information for a delivery location
 * @param {string} deliveryLocationId - The ID of the delivery location
 * @param {Date|null} fromDate - Optional date to start searching from
 * @returns {Promise<Object|null>} - Next appointment object or null if none found
 */
export async function getNextVisitInfo(deliveryLocationId, fromDate = null) {
	return new Promise((resolve) => {
		const service = iframeDataBridge.getService().sales.getAppointments;
		const currentUser = iframeDataBridge.getCurrentUser();
		const dateHelper = iframeDataBridge.getDateHelper();
		const enumAppointment = iframeDataBridge.enumAppointment;

		if (!service || !currentUser || !dateHelper || !enumAppointment) {
			resolve(null);
			return;
		}

		const start = fromDate ? new Date(fromDate) : new Date();
		const startDate = new Date(start);
		startDate.setHours(0, 0, 0, 0);

		const endDate = new Date(startDate);
		endDate.setDate(endDate.getDate() + 365);
		endDate.setHours(23, 59, 59, 999);

		const data = {
			startDate: startDate.toISOString(),
			endDate: endDate.toISOString(),
			userId: currentUser.id,
		};

		const success = (appointments) => {
			const now = fromDate ? new Date(fromDate) : new Date();
			const filtered = (appointments || []).filter((apt) => {
				if (apt.deliveryLocationId !== deliveryLocationId) return false;
				if (apt.status === enumAppointment.status.CANCELLED) return false;
				return dateHelper.isDateGreater(apt.startDate, now, true);
			});

			const nextAppointment = filtered.sort((a, b) => new Date(a.startDate) - new Date(b.startDate))[0];
			resolve(nextAppointment || null);
		};

		if (iframeDataBridge.isOnline()) {
			axios({
				method: service.type.toLowerCase(),
				url: service.getUrl(),
				headers: { Token: currentUser.tokenSales },
				[service.type.toUpperCase() === 'GET' ? 'params' : 'data']: data,
			})
				.then((res) => success(res.data))
				.catch(() => resolve(null));
		} else {
			iframeDataBridge.getStorage().get('SFA_APPOINTMENTS', (appointments) => {
				success(appointments);
			});
		}
	});
}

/** Gets the main KPIs for a delivery location
 * @param {string} deliveryLocationId - The ID of the delivery location
 * @param {Date|null} appointmentDate - Optional appointment date
 * @param {Object|null} baseAppointment - Optional base appointment object
 * @returns {Promise<Array>} - Array of KPI objects
 */
export async function getDeliveryLocationKPIs(deliveryLocationId, baseAppointment = null, selectedKpiRefs = null) {
	return new Promise(async (resolve) => {
		const sfaHelper = iframeDataBridge.getSfaHelper();
		if (!sfaHelper || !sfaHelper.kpi) {
			resolve([]);
			return;
		}

		const ecosVM = iframeDataBridge.getEcosVM();
		const deliveryLocation = ecosVM.deliveryLocations.find((dl) => dl.id === deliveryLocationId);
		if (!deliveryLocation || !deliveryLocation.clusterId) {
			resolve([]);
			return;
		}

		try {
			const main3Kpis = sfaHelper.kpi.getMains3KPIs();
			const sourceAppointment = baseAppointment || null;
			const statements = sourceAppointment && sourceAppointment._statements ? sourceAppointment._statements : null;

			if (statements && statements.length > 0) {
				const circularKpis = [];
				const allProductsAssort = ecosVM.productsAssort || [];
				const pdtsAssortIds = allProductsAssort.map((p) => p.id);
				const globalSource = sfaHelper.kpi.business.getGlobalProductsAndShelfSize(statements, pdtsAssortIds);

				for (let kpiRef of main3Kpis) {
					const kpiDefinition = ecosVM.kpis.find((k) => k.reference === kpiRef && k.activated);
					if (!kpiDefinition) continue;

					let kpiValue = 0;
					const products = globalSource.products;
					const shelfSize = globalSource.shelfSize;

					switch (kpiRef) {
						case 'DetentionRate':
							kpiValue = sfaHelper.kpi.business.detentionRate.getValue(products);
							break;
						case 'ImplantationQuality':
							kpiValue = sfaHelper.kpi.business.implantationQuality.getValue(products);
							break;
						case 'ShareOfShelf':
							kpiValue = sfaHelper.kpi.business.shareOfShelf.getValue(products, shelfSize);
							break;
						case 'TotalCode':
							kpiValue = sfaHelper.kpi.business.totalCode.getValue(products);
							break;
						case 'TotalPicking':
							kpiValue = sfaHelper.kpi.business.totalPicking.getValue(products);
							break;
						case 'TotalMissingProduct':
							kpiValue = sfaHelper.kpi.business.totalMissingProduct.getValue(products);
							break;
						case 'OOSRate':
							kpiValue = sfaHelper.kpi.business.oosRate.getValue(products);
							break;
						case 'TotalOOSProduct':
							kpiValue = sfaHelper.kpi.business.totalOOSProduct.getValue(products);
							break;
						default:
							continue;
					}

					const objective = sfaHelper.kpi.business.getGlobalObjective(deliveryLocationId, kpiRef, globalSource.ptList);
					if (objective <= 0) continue;

					const kpiItem = {
						value: Math.round(kpiValue * 100) / 100,
						objective: objective,
					};

					sfaHelper.kpi.defineColors(kpiDefinition, kpiItem, 0);
					kpiItem.pct = objective > 0 ? Math.round((kpiItem.value * 100) / objective) : 0;

					circularKpis.push({
						name: kpiRef,
						value: kpiItem.value,
						objective: kpiItem.objective,
						percentage: kpiItem.pct || 0,
						color: getKPIColor(kpiItem.pct || 0),
						infos: `${kpiItem.value}/${kpiItem.objective}`,
					});
				}

				resolve(circularKpis);
				return;
			}

			sfaHelper.kpi.getDeliveryLocationKPIs((kpis) => {
				const mains = selectedKpiRefs || sfaHelper.kpi.getMains3KPIs();
				const mainKpisData = (kpis || []).filter((kpi) => mains.includes(kpi.reference) && kpi.source === 'default');

				const circularKpis = mainKpisData
					.filter((kpi) => kpi.objective > 0 && kpi.value !== undefined)
					.map((kpi) => {
						const kpiDefinition = ecosVM.kpis.find((k) => k.reference === kpi.reference);

						const kpiItem = {
							value: kpi.value,
							objective: kpi.objective,
						};

						if (kpiDefinition) {
							sfaHelper.kpi.defineColors(kpiDefinition, kpiItem, 0);
						}

						kpiItem.pct = kpiItem.objective > 0 ? Math.round((kpiItem.value * 100) / kpiItem.objective) : 0;

						return {
							name: kpi.reference,
							value: kpi.value,
							objective: kpi.objective,
							percentage: kpiItem.pct || 0,
							color: getKPIColor(kpiItem.pct || 0),
							infos: kpi.infos || `${kpi.value}/${kpi.objective}`,
						};
					});

				resolve(circularKpis);
			}, deliveryLocationId);
		} catch (e) {
			console.error('Error in getDeliveryLocationKPIs:', e);
			resolve([]);
		}
	});
}

/** Hydrates delivery locations with selected KPIs
 * @param {Array} locations - Array of delivery location objects to hydrate
 * @param {Array} selectedKpiIds - Array of selected KPI IDs to fetch
 */
export async function hydrateLocationsKpis(locations, selectedKpiIds) {
	const ids = (selectedKpiIds || []).filter((id) => id !== null && id !== undefined);
	if (ids.length === 0) return;

	const ecosVM = iframeDataBridge.getEcosVM();
	const kpisById = new Map((ecosVM.kpis || []).map((k) => [k.id, k]));
	const selectedRefs = ids
		.map((id) => kpisById.get(id))
		.filter((k) => k && k.reference)
		.map((k) => k.reference);
	const uniqueDeliveryLocationIds = [...new Set((locations || []).map((l) => l.id).filter(Boolean))];

	const results = await Promise.all(
		uniqueDeliveryLocationIds.map(async (deliveryLocationId) => {
			const kpis = await getDeliveryLocationKPIs(deliveryLocationId, null, selectedRefs);
			return { deliveryLocationId, kpis: kpis || [] };
		})
	);

	const kpisByDlId = new Map(results.map((r) => [r.deliveryLocationId, r.kpis]));

	locations.forEach((location) => {
		const list = kpisByDlId.get(location.id) || [];
		const byRef = new Map(list.map((x) => [x.name, x]));

		location._kpis = location._kpis || {};

		ids.forEach((id) => {
			const def = kpisById.get(id);
			if (!def || !def.reference) return;

			const item = byRef.get(def.reference);
			if (!item) return;

			location._kpis[id] = {
				value: item.value,
				obj: item.objective,
			};
		});
	});
}

/** Gets the last visit information for a delivery location
 * @param {string} deliveryLocationId - The ID of the delivery location
 * @returns {Promise<Object|null>} - Last appointment object or null if none found
 */
export async function getLastVisitInfo(deliveryLocationId, deliveryLocationIds = null) {
	return new Promise((resolve) => {
		const sfaHelper = iframeDataBridge.getSfaHelper();
		if (!sfaHelper) {
			resolve(null);
			return;
		}

		sfaHelper.getLastAppointmentsWithStatements(deliveryLocationIds || [deliveryLocationId], null, 1, null, (appointments) => {
			resolve(appointments && appointments.length > 0 && !deliveryLocationIds ? appointments[0] : appointments || null);
		});
	});
}

/** Resets detail marker data while preserving lat/lng
 * @param {Object} detailMarkerData - Reactive detail marker data object
 * @param {Object|null} markerData - Marker data to fallback lat/lng if needed
 */
export function resetDetailMarkerData(detailMarkerData, markerData) {
	if (!detailMarkerData || !detailMarkerData.value) return;

	const preservedLat = detailMarkerData.value.lat;
	const preservedLng = detailMarkerData.value.lng;

	Object.keys(detailMarkerData.value).forEach((key) => {
		if (key !== 'lat' && key !== 'lng') {
			detailMarkerData.value[key] = null;
		}
	});

	if (!detailMarkerData.value.lat && preservedLat) {
		detailMarkerData.value.lat = preservedLat;
	}

	if (!detailMarkerData.value.lng && preservedLng) {
		detailMarkerData.value.lng = preservedLng;
	}

	if (!detailMarkerData.value.lat || !detailMarkerData.value.lng) {
		detailMarkerData.value.lat = markerData?.lat || markerData?._geo?.latitude || null;
		detailMarkerData.value.lng = markerData?.lng || markerData?._geo?.longitude || null;
	}
}
